import {
  Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import {
  debounceTime, distinctUntilChanged, filter, map, takeUntil,
} from 'rxjs/operators';
import { ProjectsService } from '../../../../../core/projects.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { AuthService } from '../../../../../../../shared/services';
import { PunchlistItemFormComponent } from './punchlist-item-form.component';
import { PunchlistItemDetailComponent } from './punchlist-item-detail.component';
import { canModifyPunchlistItem } from './punchlist-item-permissions';
import { Ability, subject } from '@casl/ability';
import { projectDetails } from '../../../../../core/projects.selectors';
import { projectForRoute } from '../../../../../../../shared/helpers/project-widget-config';
import {
  punchlistItemFieldMap,
  PunchlistItemField,
  PunchlistItemFieldMap,
  PunchlistItemFieldName,
  resolvePunchlistItemFields,
} from '../../../../../../../shared/helpers/project-punchlist-item-config';
import {
  punchlistItemValuesText,
  PunchlistMultiValueField,
} from '../../../../../../../shared/helpers/punchlist-item-values';

// Punch list items follow the issue workflow: Draft, Submitted, Forwarded, Responded, Closed.
// verified / sent_back / rejected are legacy values kept only so older rows still get a chip.
const STATUS_MAP: Record<string, { label: string; bgColor: string; textColor: string }> = {
  draft: { label: 'Draft', bgColor: 'bg-draft', textColor: 'txt-draft' },
  submitted: { label: 'Submitted', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
  sent_to_contractor: { label: 'Forwarded', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
  responded: { label: 'Responded', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
  closed: { label: 'Closed', bgColor: 'bg-approved', textColor: 'txt-approved' },
  verified: { label: 'Verified', bgColor: 'bg-approved', textColor: 'txt-approved' },
  sent_back: { label: 'Sent back', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
  rejected: { label: 'Rejected', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
};

// Chip colours for the system default severities. A project or organization
// can have its own severity list; a value without an entry here is shown as
// plain text.
const SEVERITY_MAP: Record<string, { label: string; bgColor: string; textColor: string }> = {
  Low: { label: 'Low', bgColor: 'bg-low', textColor: 'txt-low' },
  Medium: { label: 'Medium', bgColor: 'bg-medium', textColor: 'txt-medium' },
  High: { label: 'High', bgColor: 'bg-high', textColor: 'txt-high' },
  Critical: { label: 'Critical', bgColor: 'bg-critical', textColor: 'txt-critical' },
};

@Component({
  selector: 'app-punchlist-items-list',
  templateUrl: './punchlist-items-list.component.html',
  styleUrls: ['./punchlist-items-list.component.scss'],
  standalone: false,
})
export class PunchlistItemsListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectId: string;
  canCreate = false;
  @Input() punchlist: any;
  @Input() punchlists: any[] = [];
  @Output() punchlistChange = new EventEmitter<any>();

  items: any[] = [];
  totalRecords = 0;
  loading = false;
  selectedRow: any = null;

  form: FormGroup;
  pagination = { start: 0, pageSize: 25 };
  selectedStatuses: string[] = [];
  /**
   * The item fields this project shows: its punchlist_item_config when it has
   * one, the built-in fields otherwise (same list as the item form). Null
   * until the project is in the store; the table waits for it, so a column
   * the project hides never shows.
   */
  fields: PunchlistItemField[] | null = null;
  fieldByName: PunchlistItemFieldMap = {};
  sortByColumns: string[] = [];
  /** A sort option is listed under the project's label for its field, if it has one. */
  sortLabels: Record<string, string> = {};
  selectedSortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'desc';
  private readonly sortFieldMap: Record<string, string> = {
    'Item #': 'item_number',
    'Title': 'title',
    'Category': 'category',
    'Severity': 'severity',
    'Status': 'status',
    'Need By': 'need_by_date',
    'Created': 'created_at',
  };

  statusList = [
    { label: 'Draft', value: 'draft', isChecked: false },
    { label: 'Submitted', value: 'submitted', isChecked: false },
    { label: 'Forwarded', value: 'sent_to_contractor', isChecked: false },
    { label: 'Responded', value: 'responded', isChecked: false },
    { label: 'Closed', value: 'closed', isChecked: false },
  ];

  statusMap = STATUS_MAP;
  severityMap = SEVERITY_MAP;
  displayedColumns: string[] = [];

  account: any;
  role = '';

  @ViewChild('sortSelect') sortSelect?: MatSelect;

  /**
   * Deep link from global search: `?itemId=` auto-opens the punchlist item's
   * details panel on load, one-shot, the same way a row click would.
   */
  private pendingDeepLinkItemId: string = null;
  /** Set when the deep-linked item wasn't on the fetched page: after the
   * list refetches (via the search box seeded with the item's title), the
   * matching row gets the selected styling and is scrolled into view. */
  private pendingHighlightItemId: string = null;

  private readonly onDestroy: Subject<any> = new Subject<any>();
  private readonly store = inject(Store);

  constructor(
    private route: ActivatedRoute,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private authService: AuthService,
    private ability: Ability,
  ) {
    this.form = new FormGroup({ searchText: new FormControl('') });
    this.applyFields(null);
  }

  ngOnInit(): void {
    this.pendingDeepLinkItemId = this.route.snapshot.queryParamMap.get('itemId');
    this.account = this.authService.getCurrentUser();
    this.role = this.account?.roles?.find((r: any) => r.project_id === this.projectId)?.role || '';
    this.canCreate = this.ability.can('create', subject('PunchlistItem', { project_id: this.projectId }));

    // The store keeps whichever project was fetched last and nothing while a
    // fetch is in flight: only this list's project counts, and the fields it
    // gave stand through a refetch.
    this.store.select(projectDetails)
      .pipe(
        takeUntil(this.onDestroy),
        map(project => projectForRoute(project, this.projectId)),
        filter(project => !!project),
        map(project => resolvePunchlistItemFields(project)),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      )
      .subscribe((fields: PunchlistItemField[]) => this.applyFields(fields));

    this.form.get('searchText')?.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(() => { this.pagination.start = 0; this.fetchItems(); });

    // Status filter via query param — same pattern as warranty claims
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe((qp: any) => {
        const next = qp.status
          ? (Array.isArray(qp.status) ? qp.status : [qp.status])
          : [];
        const changed = next.length !== this.selectedStatuses.length
          || next.some((s: string) => !this.selectedStatuses.includes(s));
        if (changed) {
          this.selectedStatuses = next;
          this.pagination.start = 0;
          this.fetchItems();
        }
      });

    this.projectsService.punchlistChanged
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.fetchItems());

    if (this.punchlist) this.fetchItems();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['punchlist']?.currentValue && this.punchlist?.id) {
      // a new punchlist starts on its first page; a stale offset from the previous
      // list (e.g. page 2 = offset 25) would return nothing for a shorter list
      this.pagination.start = 0;
      this.selectedRow = null;
      this.fetchItems();
    }
  }

  /**
   * Columns and sort options follow the project's fields: Category, Severity
   * and Need By are there only when the project shows the field. Until the
   * fields are known (`fields` null) only the options no config can hide are
   * offered, and the table is not rendered.
   */
  private applyFields(fields: PunchlistItemField[] | null): void {
    const tableWasWaiting = !this.fields;
    this.fields = fields;
    this.fieldByName = punchlistItemFieldMap(fields);
    const shown = (name: PunchlistItemFieldName) => !!this.fieldByName[name];

    this.displayedColumns = [
      'item_number',
      'title',
      ...(shown('category') ? ['category'] : []),
      ...(shown('severity') ? ['severity'] : []),
      'status',
      ...(shown('need_by_date') ? ['need_by_date'] : []),
      'created_by',
      'created_at',
    ];
    this.sortByColumns = [
      'Item #',
      'Title',
      ...(shown('category') ? ['Category'] : []),
      ...(shown('severity') ? ['Severity'] : []),
      'Status',
      ...(shown('need_by_date') ? ['Need By'] : []),
      'Created',
    ];
    this.sortLabels = {
      'Title': this.fieldByName.title?.label || 'Title',
      'Category': this.fieldByName.category?.label || 'Category',
      'Severity': this.fieldByName.severity?.label || 'Severity',
      'Need By': this.fieldByName.need_by_date?.label || 'Need By',
    };

    // Sorted by a column the project has since hidden: back to the default order.
    if (this.selectedSortColumn && !this.sortByColumns.includes(this.selectedSortColumn)) {
      this.selectedSortColumn = null;
      this.fetchItems();
    }

    // A deep-linked row was selected before the table could render.
    if (fields && tableWasWaiting && this.selectedRow) {
      this.scrollHighlightedRowIntoView();
    }
  }

  /** The row's categories or types (an item can hold several), joined with ", ". */
  valuesText(row: Record<string, unknown>, field: PunchlistMultiValueField): string {
    return punchlistItemValuesText(row, field);
  }

  fetchItems(): void {
    if (!this.punchlist?.uuid && !this.punchlist?.id) return;
    this.loading = true;
    const payload: any = {
      projectId: this.projectId,
      punchlistId: this.punchlist.uuid || this.punchlist.id,
      start: this.pagination.start,
      total: this.pagination.pageSize,
    };
    const search = this.form.get('searchText')?.value?.trim();
    if (search) payload.search = search;
    if (this.selectedStatuses.length) payload.status = this.selectedStatuses;
    if (this.selectedSortColumn) {
      const field = this.sortFieldMap[this.selectedSortColumn];
      if (field) {
        payload.sort_by = field;
        payload.sort_order = this.sortDirection;
      }
    }

    this.projectsService.fetchPunchlistItems(payload)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          this.loading = false;
          // Spread the resource then attributes — attributes win so `type` is the
          // punchlist item's classification, not the JSON:API resource type.
          const rows = (resp?.data || []).map((r: any) => ({ ...r, ...(r.attributes || {}) }));
          this.items = rows;
          this.totalRecords = resp?.meta?.totalRecords || rows.length;

          // Each response rebuilds the row objects, so re-point the reference
          // the highlighted-row styling compares against.
          if (this.selectedRow) {
            const selectedId = this.selectedRow.uuid || this.selectedRow.id;
            const current = rows.find(row => (row?.uuid || row?.id) === selectedId);
            if (current) {
              this.selectedRow = current;
            }
          }

          this.tryOpenDeepLinkedItem();
          this.tryHighlightPendingItem();
        },
        error: () => { this.loading = false; },
      });
  }

  /**
   * Auto-opens the details panel for a deep-linked punchlist item (?itemId=)
   * once the items list has loaded, reusing the row-click data path. If the
   * item isn't on the fetched page (server-side pagination), it is fetched by
   * id instead; an unknown/404 id no-ops silently.
   */
  private tryOpenDeepLinkedItem(): void {
    if (!this.pendingDeepLinkItemId) {
      return;
    }
    const id = this.pendingDeepLinkItemId;
    this.pendingDeepLinkItemId = null;

    const match = this.items.find(row => `${row?.uuid || row?.id}` === `${id}`);
    if (match) {
      this.rowClicked(match);
      this.scrollHighlightedRowIntoView();
      return;
    }

    this.projectsService.fetchPunchlistItem({ itemId: id })
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: resp => {
          const record = resp?.data
            ? { ...resp.data, ...(resp.data.attributes || {}) }
            : null;
          if (record?.id) {
            this.rowClicked(record);
            // The item isn't on the fetched page (server-side pagination):
            // surface it by seeding the list's own search with its title —
            // the refetch response then highlights and scrolls to the row.
            if (record.title) {
              this.pendingHighlightItemId = id;
              this.form.get('searchText')?.setValue(record.title);
            }
          }
        },
        error: () => {
          // Deep-linked item no longer exists (or is not accessible) — stay on the list.
        },
      });
  }

  /** Applies the selected-row styling + scroll once the searched list
   * contains the deep-linked item that wasn't on the original page. */
  private tryHighlightPendingItem(): void {
    if (!this.pendingHighlightItemId) {
      return;
    }
    const row = this.items.find(r => `${r?.uuid || r?.id}` === `${this.pendingHighlightItemId}`);
    if (row) {
      this.pendingHighlightItemId = null;
      this.selectedRow = row;
      this.scrollHighlightedRowIntoView();
    }
  }

  private scrollHighlightedRowIntoView(): void {
    setTimeout(() => {
      document.querySelector('app-punchlist-items-list .highlighted-row')
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }

  punchlistPickerOpen = false;

  openPunchlistPicker(): void {
    if ((this.punchlists || []).length > 1) this.punchlistPickerOpen = true;
  }

  selectPunchlist(p: any): void {
    this.punchlistPickerOpen = false;
    if (p && (p.uuid || p.id) !== (this.punchlist?.uuid || this.punchlist?.id)) this.punchlistChange.emit(p);
  }

  clearSearch(): void {
    this.form.get('searchText')?.setValue('');
  }

  onSortColumnChange(event: any): void {
    this.selectedSortColumn = event?.value ?? null;
    this.sortDirection = 'desc';
    this.pagination.start = 0;
    this.fetchItems();
  }

  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.fetchItems();
  }

  removeSort(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedSortColumn = null;
    this.fetchItems();
  }

  openSortDropdown(event?: Event): void {
    // Material renders the select panel and its backdrop as an inline popover
    // *inside* this wrapper, so the click that closes the panel bubbles back
    // here and would reopen it. Only react to clicks on the button itself.
    const target = event?.target as HTMLElement | null;
    if (target?.closest('.cdk-overlay-popover, .cdk-overlay-backdrop')) return;
    setTimeout(() => this.sortSelect?.open());
  }

  onPageChanged(page: number): void {
    this.pagination.start = (page - 1) * this.pagination.pageSize;
    this.fetchItems();
  }

  createItem(): void {
    this.projectsService.openRightPanel.next({
      component: PunchlistItemFormComponent,
      // W74 review I2: a new item from this list's view defaults to this list
      data: { projectId: this.projectId, punchlist: this.punchlist, punchlists: this.punchlists, fromListView: true },
    });
  }

  rowClicked(row: any): void {
    this.selectedRow = row;
    // Only drafts open in the edit form; anything submitted opens read-only (workflow actions only).
    if (canModifyPunchlistItem(row)) {
      this.projectsService.openRightPanel.next({
        component: PunchlistItemFormComponent,
        data: { projectId: this.projectId, punchlist: this.punchlist, punchlists: this.punchlists, editItem: { ...row } },
      });
    } else {
      this.projectsService.openRightPanel.next({
        component: PunchlistItemDetailComponent,
        data: { ...row, projectId: this.projectId, punchlistId: this.punchlist?.uuid || this.punchlist?.id },
      });
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
